import { useLayoutEffect, useRef, useState } from 'react';

type UseWordClampOptions = {
  lines?: number;
  ellipsis?: string; // '…' or '...'
  allowBreakOnVeryLongFirstToken?: boolean;
  debug?: boolean;
};

function getLineHeightPx(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const lh = cs.lineHeight;
  if (lh === 'normal') return Math.max(1, parseFloat(cs.fontSize) * 1.2);
  const n = parseFloat(lh);
  return Number.isFinite(n) && n > 0 ? n : Math.max(1, parseFloat(cs.fontSize) * 1.2);
}

// Remove trailing spaces; if trailing punctuation exists, drop the final spaces before ellipsis.
// Optionally, you can also strip the final punctuation itself if product prefers "...", not ",…".
function tightenBeforeEllipsis(s: string) {
  // Remove trailing spaces
  s = s.replace(/\s+$/u, '');
  // If we have "..., " or "word, " => remove the space after punctuation
  s = s.replace(/([,.;:!?])\s+$/u, '$1');
  return s;
}

function clampUsingElement(
  el: HTMLElement,
  fullText: string,
  desiredLines: number,
  ellipsis: string,
  allowBreakOnVeryLongFirstToken: boolean,
  debug: boolean
) {
  if (!fullText) return '';

  // Ensure measurable multi-line context
  const prevDisplay = el.style.display;
  const prevWhiteSpace = el.style.whiteSpace;
  if (!prevDisplay) el.style.display = 'block';
  if (!prevWhiteSpace) el.style.whiteSpace = 'normal';

  // Measure with full text
  el.textContent = fullText;

  const lineHeight = getLineHeightPx(el);
  const clientH = el.clientHeight;
  const eps = 0.75; // Slightly larger epsilon for sub-pixel safety

  if (!clientH || !lineHeight) {
    if (debug) console.warn('[useWordClamp] zero clientHeight/lineHeight', { clientH, lineHeight });
    el.style.display = prevDisplay;
    el.style.whiteSpace = prevWhiteSpace;
    return fullText;
  }

  // We still compute allowedLines in case height allows fewer than requested
  let allowedLines = Math.max(1, Math.floor(clientH / lineHeight));
  allowedLines = Math.min(allowedLines, desiredLines);

  // If full text already fits, return it
  if (el.scrollHeight <= clientH + eps) {
    if (debug) console.log('[useWordClamp] fits (client-based)', {
      clientH, scrollH: el.scrollHeight, lineHeight, allowedLines
    });
    el.style.display = prevDisplay;
    el.style.whiteSpace = prevWhiteSpace;
    return fullText;
  }

  const words = fullText.trim().split(/\s+/);

  // Binary search for a close fit
  let lo = 1, hi = words.length, best = 0;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const base = words.slice(0, mid).join(' ');
    const candidate = tightenBeforeEllipsis(base) + ellipsis;

    el.textContent = candidate;

    if (el.scrollHeight <= clientH + eps) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  // If even one word + ellipsis can't fit
  if (best === 0) {
    if (allowBreakOnVeryLongFirstToken) {
      const prevWB = el.style.wordBreak;
      const prevOW = el.style.overflowWrap;
      el.style.wordBreak = 'break-all';
      el.style.overflowWrap = 'anywhere';
      const candidate = tightenBeforeEllipsis(words[0]) + ellipsis;
      el.textContent = candidate;
      const result = el.scrollHeight <= clientH + eps ? candidate : ellipsis;
      el.style.wordBreak = prevWB;
      el.style.overflowWrap = prevOW;

      if (debug) console.log('[useWordClamp] long-first-token fallback', {
        result, clientH, scrollH: el.scrollHeight
      });

      el.style.display = prevDisplay;
      el.style.whiteSpace = prevWhiteSpace;
      return result;
    } else {
      el.style.display = prevDisplay;
      el.style.whiteSpace = prevWhiteSpace;
      return ellipsis;
    }
  }

  // Build the initial result from the binary search
  let finalBase = words.slice(0, best).join(' ');
  let final = tightenBeforeEllipsis(finalBase) + ellipsis;

  // Post-check shrink-to-fit loop: if it still overflows, drop one more word at a time
  el.textContent = final;
  while (el.scrollHeight > clientH + eps && best > 1) {
    best -= 1;
    finalBase = words.slice(0, best).join(' ');
    final = tightenBeforeEllipsis(finalBase) + ellipsis;
    el.textContent = final;
  }

  if (debug) console.log('[useWordClamp] clamped (client-based)', {
    result: final,
    clientH,
    scrollH: el.scrollHeight,
    lineHeight,
    allowedLines,
    bestCount: best
  });

  el.style.display = prevDisplay;
  el.style.whiteSpace = prevWhiteSpace;
  return final;
}

export function useWordClamp(
  ref: React.RefObject<HTMLElement>,
  text: string,
  options: UseWordClampOptions = {}
) {
  const {
    lines = 2,
    ellipsis = '…', // You can switch to '...' if your font has issues with U+2026
    allowBreakOnVeryLongFirstToken = false,
    debug = false
  } = options;

  const [renderText, setRenderText] = useState(text);
  const rafId = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const recompute = () => {
      const result = clampUsingElement(
        el, text, lines, ellipsis, allowBreakOnVeryLongFirstToken, debug
      );
      setRenderText(result);
    };

    const scheduleAfterPaint = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        rafId.current = requestAnimationFrame(recompute);
      });
    };

    // Initial run after fonts load (for stable metrics)
    const init = () => scheduleAfterPaint();
    if ((document as any).fonts?.ready) {
      (document as any).fonts.ready.then(init);
    } else {
      init();
    }

    // Resize & visibility observers
    const ro = new ResizeObserver(() => scheduleAfterPaint());
    ro.observe(el);

    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) scheduleAfterPaint();
    });
    io.observe(el);

    const onWinResize = () => scheduleAfterPaint();
    window.addEventListener('resize', onWinResize);

    return () => {
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('resize', onWinResize);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [ref, text, lines, ellipsis, allowBreakOnVeryLongFirstToken, debug]);

  return renderText;
}